import React, { useState, useRef, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import {
  Bars3Icon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const navItems = [
  { to: '/como-funciona', label: 'Como funciona' },
  { to: '/precos', label: 'Planos' },
  { to: '/para-empresas', label: 'Para empresas' },
  { to: '/regioes-atendidas', label: 'Cobertura' },
  { to: '/sobre', label: 'Sobre' },
];

const Header = () => {
  const [isMobileNavVisible, setIsMobileNavVisible] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const mobileNavRef = useRef();
  const hamburgerButtonRef = useRef();
  const location = useLocation();

  const closeMobile = () => setIsMobileNavVisible(false);
  const toggleMobileNav = () => setIsMobileNavVisible((p) => !p);

  useEffect(() => { closeMobile(); }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        mobileNavRef.current &&
        !mobileNavRef.current.contains(event.target) &&
        hamburgerButtonRef.current &&
        !hamburgerButtonRef.current.contains(event.target)
      ) {
        setIsMobileNavVisible(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const ctaTo = location.pathname === '/' ? '#contato' : '/#contato';

  return (
    <header
      className={`sticky top-0 z-40 transition-all duration-300 border-b
        ${scrolled
          ? 'bg-white/90 backdrop-blur-md border-ink-100 shadow-soft'
          : 'bg-white border-transparent'}`}
    >
      <ToastContainer position="top-right" theme="colored" />

      <div className="container mx-auto flex items-center justify-between px-4 lg:px-6 py-4">
        <div className="flex items-center gap-8">
          <Link to="/" className="flex items-center shrink-0" aria-label="Fretz - Home">
            <img src="/fretz-logo-light.png" alt="Fretz" className="h-9 w-auto" />
          </Link>

          <nav className="hidden lg:flex items-center">
            <ul className="flex items-center gap-7">
              {navItems.map((item) => (
                <li key={item.to}>
                  <NavLink to={item.to} className={({ isActive }) => `nav-link ${isActive ? 'active text-brand-700' : ''}`}>
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <Link to="/quero-ser-entregador" className="text-sm font-semibold text-ink-700 hover:text-brand-700 transition">
            Trabalhe nas rotas
          </Link>
          <a href={ctaTo} className="btn-accent">
            Quero Fretz Empresas
          </a>
        </div>

        <button
          onClick={toggleMobileNav}
          className="hamburger-button block lg:hidden"
          ref={hamburgerButtonRef}
          aria-label={isMobileNavVisible ? 'Fechar menu' : 'Abrir menu'}
          aria-expanded={isMobileNavVisible}
        >
          {isMobileNavVisible
            ? <XMarkIcon className="h-6 w-6 text-ink-900" />
            : <Bars3Icon className="h-6 w-6 text-ink-900" />}
        </button>
      </div>

      <div
        className={`mobile-nav ${isMobileNavVisible ? 'visible' : ''}`}
        ref={mobileNavRef}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center justify-between mb-6">
          <img src="/fretz-logo-light.png" alt="Fretz" className="h-9 w-auto" />
          <button onClick={closeMobile} className="p-2 rounded-lg hover:bg-ink-100 text-ink-700" aria-label="Fechar menu">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <ul>
          <li>
            <NavLink to="/" end className={({ isActive }) => (isActive ? 'active' : undefined)}>
              Início
            </NavLink>
          </li>
          {navItems.map((item) => (
            <li key={item.to}>
              <NavLink to={item.to} className={({ isActive }) => (isActive ? 'active' : undefined)}>
                {item.label}
              </NavLink>
            </li>
          ))}
          <li>
            <NavLink to="/quero-ser-entregador" className={({ isActive }) => (isActive ? 'active' : undefined)}>
              Trabalhe nas rotas
            </NavLink>
          </li>
          <li className="mt-4 px-2">
            <a href={ctaTo} className="btn-accent w-full" onClick={closeMobile}>
              Quero Fretz Empresas
            </a>
          </li>
        </ul>
      </div>

      {isMobileNavVisible && (
        <div
          className="fixed inset-0 bg-ink-900/40 backdrop-blur-sm z-[999] lg:hidden"
          onClick={closeMobile}
          aria-hidden="true"
        />
      )}
    </header>
  );
};

export default Header;
